/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useIsLoggedIn } from '../Store/user/hooks';
import { useUserData } from '../hooks';
import { UsePortalDispatch, UsePortalSelector } from '../Store';
import AnnouncementBanner, { ANNOUNCEMENT_BANNER_HEIGHT } from './AnnouncementBanner';
import AnnouncementPopup from './AnnouncementPopup';
import { navigateBannerLink } from './ctaApi';
import { isWithinCtaSchedule } from './scheduleUtils';
import {
	clearPendingLoginPopup,
	dismissBanner,
	getPendingLoginPopupVersion,
	isCtaContentLoaded,
} from './ctaStorage';
import { setCtaPopupDismissedVersion } from '../Store/user';
import type { CtaBannerId } from './types';
import { getCtaBannerVersion, getCtaPopupContent } from './types';
import {
	useAnnouncementBannerVisible,
	useIsPopupPermanentlyDismissed,
	useCtaContent,
} from './hooks';

const isEditCtaPage = () => window.location.hash.toLowerCase().includes('editctaelement');

type CtaBannerViewModel = {
	id: CtaBannerId;
	text: string;
	/** Undefined when the banner has nothing to do on click, so it renders as plain text. */
	onClick?: (() => void) | undefined;
	onDismiss: () => void;
};

type CtaPopupViewModel = {
	id: CtaBannerId;
	title: string;
	body: string;
	imageUrl: string | null;
	allowHideUntilUpdate: boolean;
};

type CtaElementsContextValue = {
	banners: CtaBannerViewModel[];
	/** At most one popup is ever on screen. */
	popup: CtaPopupViewModel | null;
	onPopupClose: (hideUntilUpdate: boolean) => void;
};

const CtaElementsContext = createContext<CtaElementsContextValue | null>(null);

const useCtaElementsContext = () => {
	const context = useContext(CtaElementsContext);
	if (!context) {
		throw new Error('CtaElements components must be used within CtaElementsProvider');
	}
	return context;
};

export const CtaElementsProvider = ({ children }: { children: React.ReactNode }) => {
	const authenticated = useIsLoggedIn();
	const userData = useUserData();
	const userId = userData?.id;
	const popupDismissedVersion = UsePortalSelector((state) => state.user.cta_popup_dismissed_version);
	const dispatch = UsePortalDispatch();
	const [onEditPage, setOnEditPage] = useState(isEditCtaPage());
	const content = useCtaContent();
	const bannerVisible = useAnnouncementBannerVisible(content, 'primary', userId, authenticated);
	const bannerTwoVisible = useAnnouncementBannerVisible(content, 'secondary', userId, authenticated);
	const permanentlyDismissed = useIsPopupPermanentlyDismissed(
		content,
		userId,
		authenticated,
		popupDismissedVersion,
	);
	const [loginPopupOpen, setLoginPopupOpen] = useState(false);
	const [popupFromBanner, setPopupFromBanner] = useState<CtaBannerId | null>(null);

	const popupTwo = getCtaPopupContent(content, 'secondary');
	// The second banner is only clickable if there is a popup for it to open.
	const bannerTwoOpensPopup = Boolean(popupTwo.title.trim()) && isWithinCtaSchedule({
		start: popupTwo.scheduleStart,
		end: popupTwo.scheduleEnd,
	});

	useEffect(() => {
		const handleHashChange = () => setOnEditPage(isEditCtaPage());
		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, []);

	useEffect(() => {
		if (!authenticated) {
			setLoginPopupOpen(false);
			setPopupFromBanner(null);
			return;
		}

		if (!userId) return;

		const pendingVersion = getPendingLoginPopupVersion(userId);
		if (pendingVersion === null) return;

		if (!isCtaContentLoaded()) return;

		if (!content.popupTitle.trim()) {
			clearPendingLoginPopup(userId);
			return;
		}

		if (!isWithinCtaSchedule({
			start: content.popupScheduleStart,
			end: content.popupScheduleEnd,
		})) {
			clearPendingLoginPopup(userId);
			return;
		}

		if (permanentlyDismissed) {
			clearPendingLoginPopup(userId);
			return;
		}

		if (pendingVersion === content.version) {
			clearPendingLoginPopup(userId);
			setLoginPopupOpen(true);
		}
	}, [
		authenticated,
		content.popupScheduleEnd,
		content.popupScheduleStart,
		content.popupTitle,
		content.version,
		permanentlyDismissed,
		userId,
	]);

	const showBanner = authenticated && !onEditPage && bannerVisible;
	const showBannerTwo = authenticated && !onEditPage && bannerTwoVisible;
	// The login popup is always the primary one; a banner click can open either.
	const openPopupId: CtaBannerId | null = (loginPopupOpen ? 'primary' : popupFromBanner);
	const activePopupId = authenticated && !onEditPage ? openPopupId : null;

	const value = useMemo<CtaElementsContextValue>(() => {
		const banners: CtaBannerViewModel[] = [];

		if (showBanner) {
			banners.push({
				id: 'primary',
				text: content.bannerText,
				onClick: () => {
					const link = content.bannerLinkUrl?.trim();
					if (link) {
						navigateBannerLink(link);
						return;
					}
					setPopupFromBanner('primary');
				},
				onDismiss: () => {
					if (userId) {
						dismissBanner(userId, getCtaBannerVersion(content, 'primary'), 'primary');
					}
					setPopupFromBanner((current) => (current === 'primary' ? null : current));
				},
			});
		}

		if (showBannerTwo) {
			const linkTwo = content.bannerTwoLinkUrl?.trim();
			let onClickTwo: (() => void) | undefined;
			if (linkTwo) {
				onClickTwo = () => navigateBannerLink(linkTwo);
			} else if (bannerTwoOpensPopup) {
				onClickTwo = () => setPopupFromBanner('secondary');
			}

			banners.push({
				id: 'secondary',
				text: content.bannerTwoText,
				onClick: onClickTwo,
				onDismiss: () => {
					if (userId) {
						dismissBanner(userId, getCtaBannerVersion(content, 'secondary'), 'secondary');
					}
					setPopupFromBanner((current) => (current === 'secondary' ? null : current));
				},
			});
		}

		let popup: CtaPopupViewModel | null = null;
		if (activePopupId) {
			const popupContent = getCtaPopupContent(content, activePopupId);
			popup = {
				id: activePopupId,
				title: popupContent.title,
				body: popupContent.body,
				imageUrl: popupContent.imageUrl,
				// Only the primary popup appears unprompted, so only it can be opted out of.
				allowHideUntilUpdate: activePopupId === 'primary',
			};
		}

		return {
			banners,
			popup,
			onPopupClose: (hideUntilUpdate: boolean) => {
				setLoginPopupOpen(false);
				setPopupFromBanner(null);
				if (hideUntilUpdate && activePopupId === 'primary') {
					dispatch(setCtaPopupDismissedVersion(content.version));
				}
			},
		};
	}, [
		activePopupId,
		bannerTwoOpensPopup,
		content.bannerText,
		content.bannerLinkUrl,
		content.bannerTwoText,
		content.bannerTwoLinkUrl,
		content.popupBody,
		content.popupImageUrl,
		content.popupTitle,
		content.popupTwoBody,
		content.popupTwoImageUrl,
		content.popupTwoTitle,
		content.bannerVersion,
		content.bannerTwoVersion,
		content.version,
		showBanner,
		showBannerTwo,
		userId,
		dispatch,
	]);

	return (
		<CtaElementsContext.Provider value={value}>
			{children}
		</CtaElementsContext.Provider>
	);
};

export const CtaAnnouncementBanner = () => {
	const { banners } = useCtaElementsContext();
	const bannerCount = banners.length;

	// The header offsets itself by the slot height, which grows with each visible banner.
	useEffect(() => {
		const root = document.documentElement;
		if (!bannerCount) {
			root.style.removeProperty('--announcement-banner-height');
			return undefined;
		}
		root.style.setProperty(
			'--announcement-banner-height',
			`${bannerCount * ANNOUNCEMENT_BANNER_HEIGHT}px`,
		);
		return () => {
			root.style.removeProperty('--announcement-banner-height');
		};
	}, [bannerCount]);

	if (!bannerCount) return null;

	return (
		<div className="announcement-banner-slot">
			{banners.map((banner) => (
				<AnnouncementBanner
					key={banner.id}
					text={banner.text}
					onClick={banner.onClick}
					onDismiss={banner.onDismiss}
				/>
			))}
		</div>
	);
};

export const CtaAnnouncementPopup = () => {
	const { popup, onPopupClose } = useCtaElementsContext();

	if (!popup) return null;

	return (
		<AnnouncementPopup
			key={popup.id}
			title={popup.title}
			body={popup.body}
			imageUrl={popup.imageUrl}
			allowHideUntilUpdate={popup.allowHideUntilUpdate}
			checkboxId={`cta-popup-hide-until-update-${popup.id}`}
			onClose={onPopupClose}
		/>
	);
};

export default CtaElementsProvider;
